import React, { useState, useEffect } from 'react';
import {
  Autocomplete, Box, Card, CardContent, Typography, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, CircularProgress, Button, Chip, Switch, FormControlLabel, Grid,
  useTheme, useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, FilterAltOff as ClearIcon } from '@mui/icons-material';
import { reportCardAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import TablePagination from '../common/TablePagination';
import FormDialog from '../common/FormDialog';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const CATEGORY_OPTIONS = ['Core', 'Elective', 'Extra'];
const STATUS_OPTIONS = [
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' }
];

const Subjects = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.SUBJECTS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.SUBJECTS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.SUBJECTS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.SUBJECTS, ACTIONS.DELETE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [subjects, setSubjects] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [formData, setFormData] = useState({ subject_name: '', short_name: '', description: '', category: 'Core', is_active: true });
  const [filters, setFilters] = useState({ search: '', category: '', is_active: '' });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogLoading, setDialogLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  useEffect(() => {
    if (canRead) fetchSubjects();
  }, [filters, canRead]);

  const fetchSubjects = async (page = 1, perPage = null) => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage || pagination.per_page };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.is_active !== '') params.is_active = filters.is_active;
      const res = await reportCardAPI.getSubjects(params);
      if (res.data?.data) {
        setSubjects(res.data.data);
        setPagination({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          per_page: res.data.per_page,
          total: res.data.total,
        });
      } else {
        setSubjects(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching subjects', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);
    setErrors({});
    try {
      if (editMode) {
        await reportCardAPI.updateSubject(currentSubject.id, formData);
        setSnackbar({ open: true, message: 'Subject updated successfully!', severity: 'success' });
      } else {
        await reportCardAPI.storeSubject(formData);
        setSnackbar({ open: true, message: 'Subject added successfully!', severity: 'success' });
      }
      setShowModal(false);
      resetForm();
      fetchSubjects();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setSnackbar({ open: true, message: 'Please fix the validation errors', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || 'Error saving subject', severity: 'error' });
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await reportCardAPI.deleteSubject(deleteDialog.id);
      setSnackbar({ open: true, message: 'Subject deleted successfully!', severity: 'success' });
      setDeleteDialog({ open: false, id: null });
      fetchSubjects();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error deleting subject', severity: 'error' });
    }
  };

  const openModal = (subject = null) => {
    if (subject) {
      setEditMode(true);
      setCurrentSubject(subject);
      setFormData({ subject_name: subject.subject_name, short_name: subject.short_name, description: subject.description || '', category: subject.category, is_active: subject.is_active });
    } else {
      setEditMode(false);
      setCurrentSubject(null);
      setFormData({ subject_name: '', short_name: '', description: '', category: 'Core', is_active: true });
    }
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ subject_name: '', short_name: '', description: '', category: 'Core', is_active: true });
    setEditMode(false);
    setCurrentSubject(null);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', is_active: '' });
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view subjects.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Subject Management"
        subtitle="Manage all subjects for report cards"
        actions={[
          <Button key="clear-filters" variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} fullWidth={isMobile}>
            Clear Filters
          </Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchSubjects} fullWidth={isMobile}>
            Refresh
          </Button>,
          ...(canCreate ? [<Button key="add-subject" variant="contained" startIcon={<AddIcon />} onClick={() => openModal()} fullWidth={isMobile}>Add Subject</Button>] : [])
        ]}
      />

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Typography variant="h6">Subjects</Typography>
            <Chip label={subjects.length} size="small" color="primary" />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <TextField size="small" placeholder="Search..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} sx={{ minWidth: 220 }} />
            <Autocomplete
              sx={{ minWidth: { xs: 220, sm: 240, md: 260 } }}
              size="small"
              options={CATEGORY_OPTIONS}
              value={filters.category || null}
              onChange={(_, value) => setFilters({ ...filters, category: value || '' })}
              renderInput={(params) => <TextField {...params} label="Category" />}
            />
            <Autocomplete
              sx={{ minWidth: { xs: 200, sm: 220, md: 240 } }}
              size="small"
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((option) => option.value === String(filters.is_active)) || null}
              onChange={(_, value) => setFilters({ ...filters, is_active: value?.value || '' })}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Short Name</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : subjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                      No subjects found. Click the + icon to add a subject.
                    </TableCell>
                  </TableRow>
                ) : (
                  subjects.map((subject) => (
                    <TableRow key={subject.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{subject.subject_name}</TableCell>
                      <TableCell>{subject.short_name}</TableCell>
                      <TableCell>{subject.description || '-'}</TableCell>
                      <TableCell><Chip label={subject.category} size="small" color={subject.category === 'Core' ? 'primary' : subject.category === 'Elective' ? 'secondary' : 'default'} /></TableCell>
                      <TableCell>
                        <Chip 
                          label={subject.is_active ? 'Active' : 'Inactive'} 
                          color={subject.is_active ? 'success' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" onClick={() => openModal(subject)} color="primary"><EditIcon fontSize="small" /></IconButton>}
                        {canDelete && <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: subject.id })} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            pagination={pagination}
            onPageChange={(page) => fetchSubjects(page)}
            onPageSizeChange={(newPerPage) => fetchSubjects(1, newPerPage)}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      <FormDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editMode ? 'Edit Subject' : 'Add Subject'}
        onSubmit={handleSubmit}
        saving={dialogLoading}
        isMobile={isMobile}
      >
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Subject Name"
              value={formData.subject_name}
              onChange={e => setFormData({ ...formData, subject_name: e.target.value })}
              fullWidth required autoFocus
              error={!!errors.subject_name}
              helperText={errors.subject_name?.[0]}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Short Name"
              value={formData.short_name}
              onChange={e => setFormData({ ...formData, short_name: e.target.value })}
              fullWidth required
              error={!!errors.short_name}
              helperText={errors.short_name?.[0]}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              fullWidth multiline rows={2}
              error={!!errors.description}
              helperText={errors.description?.[0]}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={CATEGORY_OPTIONS}
              value={formData.category || null}
              onChange={(_, value) => setFormData({ ...formData, category: value || '' })}
              renderInput={(params) => <TextField {...params} label="Category" required error={!!errors.category} helperText={errors.category?.[0]} />}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </FormDialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this subject? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subjects;
